import type { ShopDiscoveryGarment } from "../utils/shopDiscoveryDedupe";
import type { ShopEssentialGarmentChip } from "./shopEssentialsProductSlugs";

export function discoveryGarmentToProductQueryChip(
  garment: ShopDiscoveryGarment
): ShopEssentialGarmentChip | null {
  if (garment === "tees") return "tees";
  if (garment === "hoodies") return "hoodies";
  if (garment === "sweatshirts") return "sweatshirts";
  return null;
}

/** Reserved `/shop/:segment` paths — not product category slugs. */
export const RESERVED_SHOP_ROUTE_SEGMENTS = new Set([
  "designs",
  "product",
  "sellers",
  "listings",
  "checkout",
  "recommendations",
]);

/**
 * When `/shop/:segment` is matched but the segment is reserved, redirect here.
 * Keeps static shop routes reachable even if route ranking regresses.
 */
export const RESERVED_SHOP_ROUTE_REDIRECTS: Record<string, string> = {
  designs: "/shop/designs",
  product: "/shop",
  sellers: "/shop",
  listings: "/shop",
  checkout: "/shop",
  recommendations: "/shop",
};

export function reservedShopRouteRedirect(
  slug: string | null | undefined
): string | null {
  const normalized = normalizeShopCategorySlug(slug);
  if (!normalized || !RESERVED_SHOP_ROUTE_SEGMENTS.has(normalized)) {
    return null;
  }
  return RESERVED_SHOP_ROUTE_REDIRECTS[normalized] ?? "/shop";
}

export interface ShopProductCategory {
  slug: string;
  label: string;
  garment: Exclude<ShopDiscoveryGarment, "all">;
  /** Shown in the main design grid when this category is active. */
  gridTitle: string;
  gridSubtitle: string;
  /** Always visible in navigation even if the product API is empty. */
  alwaysShow: boolean;
}

export const SHOP_PRODUCT_CATEGORIES: ShopProductCategory[] = [
  {
    slug: "t-shirts",
    label: "T-Shirts",
    garment: "tees",
    gridTitle: "T-shirt designs",
    gridSubtitle: "Fresh prints ready for your favorite T-shirt fit.",
    alwaysShow: true,
  },
  {
    slug: "hoodies",
    label: "Hoodies",
    garment: "hoodies",
    gridTitle: "Hoodie designs",
    gridSubtitle: "Cozy hoodie-ready designs, curated for everyday wear.",
    alwaysShow: true,
  },
  {
    slug: "sweatshirts",
    label: "Sweatshirts",
    garment: "sweatshirts",
    gridTitle: "Sweatshirt designs",
    gridSubtitle: "Soft fleece-ready prints for layered, relaxed looks.",
    alwaysShow: true,
  },
  {
    slug: "caps",
    label: "Caps",
    garment: "caps",
    gridTitle: "Cap designs",
    gridSubtitle: "Headwear-ready prints for everyday street style.",
    alwaysShow: false,
  },
  {
    slug: "bags",
    label: "Bags",
    garment: "bags",
    gridTitle: "Bag designs",
    gridSubtitle: "Tote and carry styles with print-ready designs.",
    alwaysShow: false,
  },
];

const BY_SLUG = new Map(
  SHOP_PRODUCT_CATEGORIES.map((category) => [category.slug, category] as const)
);

const BY_GARMENT = new Map(
  SHOP_PRODUCT_CATEGORIES.map((category) => [category.garment, category] as const)
);

/** Lowercase trimmed slug for route matching and lookups. */
export function normalizeShopCategorySlug(
  raw: string | null | undefined
): string {
  return raw?.trim().toLowerCase() ?? "";
}

export function shopPathForCategorySlug(slug: string | null | undefined): string {
  const trimmed = normalizeShopCategorySlug(slug);
  if (!trimmed) return "/shop";
  return `/shop/${trimmed}`;
}

export function shopCategoryFromSlug(
  slug: string | null | undefined
): ShopProductCategory | null {
  const normalized = normalizeShopCategorySlug(slug);
  if (!normalized) return null;
  return BY_SLUG.get(normalized) ?? null;
}

export function garmentFromCategorySlug(
  slug: string | null | undefined
): ShopDiscoveryGarment {
  return shopCategoryFromSlug(slug)?.garment ?? "all";
}

export function isKnownShopCategorySlug(slug: string): boolean {
  return BY_SLUG.has(normalizeShopCategorySlug(slug));
}

export function isReservedShopRouteSegment(slug: string): boolean {
  return RESERVED_SHOP_ROUTE_SEGMENTS.has(normalizeShopCategorySlug(slug));
}

/** True when the URL segment is not a valid marketplace category route. */
export function isInvalidShopCategoryRouteSegment(
  slug: string | null | undefined
): boolean {
  const normalized = normalizeShopCategorySlug(slug);
  if (!normalized) return false;
  if (isReservedShopRouteSegment(normalized)) return false;
  return !isKnownShopCategorySlug(normalized);
}

export function shopCategoryPathForGarmentChip(
  chip: ShopEssentialGarmentChip
): string {
  const category = BY_GARMENT.get(chip);
  return shopPathForCategorySlug(category?.slug ?? null);
}

export const SHOP_ALL_DESIGNS_GRID = {
  title: "Shop all designs",
  subtitle:
    "New prints and curated picks — ready to wear on your favorite blanks.",
} as const;
